import React, { useEffect, useState } from "react";
import { studentService, attendanceService } from "../../firebase/services";
import {
  CalendarCheck,
  CheckCircle2,
  XCircle,
  Save,
  Loader2,
  Calendar,
  AlertCircle,
  Users,
} from "lucide-react";

export const AdminAttendance = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [dayNumber, setDayNumber] = useState(1);
  const [dateStr, setDateStr] = useState(() => new Date().toISOString().split("T")[0]);
  const [sessionTopic, setSessionTopic] = useState("Introduction to Cyber Security Landscape & Threat Vectors");
  const [attendanceMap, setAttendanceMap] = useState({});

  useEffect(() => {
    const loadStudentsAndAttendance = async () => {
      setLoading(true);
      try {
        const studentList = await studentService.getStudents();
        const activeOnly = studentList.filter((s) => s.status === "active");
        setStudents(activeOnly);

        // Fetch any existing attendance for this dayNumber
        const existingRecords = await attendanceService.getAttendanceByDateAndDay(dateStr, dayNumber);
        
        const map = {};
        // Default all active students to Present or their saved record
        activeOnly.forEach((st) => {
          const match = existingRecords.find((r) => r.studentId === st.studentId);
          map[st.studentId] = match ? match.status : "Present";
        });
        setAttendanceMap(map);
      } catch (err) {
        console.error("Load attendance roster error:", err);
      } finally {
        setLoading(false);
      }
    };

    loadStudentsAndAttendance();
  }, [dayNumber, dateStr]);

  const toggleStudentAttendance = (studentId) => {
    setAttendanceMap((prev) => ({
      ...prev,
      [studentId]: prev[studentId] === "Present" ? "Absent" : "Present",
    }));
  };

  const markAll = (status) => {
    const updated = {};
    students.forEach((st) => {
      updated[st.studentId] = status;
    });
    setAttendanceMap(updated);
  };

  const handleSaveAttendance = async (e) => {
    e.preventDefault();
    if (students.length === 0) {
      alert("No active students to mark attendance for.");
      return;
    }

    setSaving(true);
    setSaveSuccess(false);

    try {
      const records = students.map((st) => ({
        studentId: st.studentId,
        studentUid: st.id,
        studentName: st.fullName,
        studentEmail: st.email,
        status: attendanceMap[st.studentId] || "Present",
      }));

      await attendanceService.saveBatchAttendance(dayNumber, dateStr, sessionTopic, records);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error("Save attendance error:", err);
      alert(err.message || "Failed to save attendance.");
    } finally {
      setSaving(false);
    }
  };

  const presentCount = Object.values(attendanceMap).filter((v) => v === "Present").length;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Masterclass Roll Call Attendance</h1>
          <p className="text-xs text-muted-foreground font-mono">
            Mark daily attendance for approved students in the 15 Days Masterclass.
          </p>
        </div>
        {saveSuccess && (
          <div
            data-testid="attendance-saved-alert"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-xs font-mono"
          >
            <CheckCircle2 className="w-4 h-4" />
            <span>Attendance saved successfully!</span>
          </div>
        )}
      </div>

      {/* Control Box: Day, Date, Topic */}
      <form onSubmit={handleSaveAttendance} className="bg-card border border-border rounded-xl p-5 space-y-4 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-mono font-medium text-foreground mb-1">
              Masterclass Day (1 - 15)
            </label>
            <select
              value={dayNumber}
              data-testid="admin-day-select"
              onChange={(e) => setDayNumber(Number(e.target.value))}
              className="w-full px-3 py-2 text-xs rounded-lg bg-background border border-input focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono font-bold text-emerald-400"
            >
              {Array.from({ length: 15 }, (_, i) => i + 1).map((d) => (
                <option key={d} value={d}>
                  Day {d}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-mono font-medium text-foreground mb-1">
              Class Date
            </label>
            <input
              type="date"
              data-testid="admin-attendance-date-input"
              value={dateStr}
              onChange={(e) => setDateStr(e.target.value)}
              className="w-full px-3 py-2 text-xs rounded-lg bg-background border border-input focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
            />
          </div>

          <div>
            <label className="block text-xs font-mono font-medium text-foreground mb-1">
              Session Topic
            </label>
            <input
              type="text"
              data-testid="admin-attendance-topic-input"
              value={sessionTopic}
              onChange={(e) => setSessionTopic(e.target.value)}
              placeholder="e.g. Hands-on Burp Suite & Web Security"
              className="w-full px-3 py-2 text-xs rounded-lg bg-background border border-input focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
            />
          </div>
        </div>

        {/* Quick Bulk Actions */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-border">
          <div className="flex items-center space-x-2 text-xs font-mono">
            <span className="text-muted-foreground">Bulk Mark:</span>
            <button
              type="button"
              onClick={() => markAll("Present")}
              data-testid="mark-all-present-btn"
              className="px-2.5 py-1 rounded bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/20 transition-colors"
            >
              All Present
            </button>
            <button
              type="button"
              onClick={() => markAll("Absent")}
              data-testid="mark-all-absent-btn"
              className="px-2.5 py-1 rounded bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/20 transition-colors"
            >
              All Absent
            </button>
          </div>

          <div className="text-xs font-mono text-muted-foreground">
            Summary: <strong className="text-emerald-400">{presentCount}</strong> / {students.length} Present
          </div>
        </div>

        {/* Student Roster */}
        <div className="border border-border rounded-xl overflow-hidden mt-4">
          {loading ? (
            <div className="p-12 text-center text-muted-foreground flex flex-col items-center justify-center gap-2">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
              <span className="text-xs font-mono">Loading active students from Firestore...</span>
            </div>
          ) : students.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground space-y-1">
              <AlertCircle className="w-6 h-6 mx-auto opacity-50" />
              <div className="text-xs font-semibold text-foreground">No approved active students.</div>
              <p className="text-[11px] font-mono">
                Approve student applications first in <code>/admin/registrations</code> to populate this roster.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {students.map((st) => {
                const status = attendanceMap[st.studentId] || "Present";
                const isPresent = status === "Present";
                return (
                  <div
                    key={st.id}
                    className="p-3.5 flex items-center justify-between hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="font-mono text-xs font-bold text-emerald-400 w-36">
                        {st.studentId}
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-foreground">{st.fullName}</div>
                        <div className="text-[10px] text-muted-foreground font-mono truncate max-w-xs">
                          {st.college}
                        </div>
                      </div>
                    </div>

                    <div>
                      <button
                        type="button"
                        onClick={() => toggleStudentAttendance(st.studentId)}
                        data-testid={`attendance-toggle-${st.studentId}`}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-mono font-semibold transition-all shadow-sm ${
                          isPresent
                            ? "bg-emerald-600 hover:bg-emerald-500 text-white"
                            : "bg-rose-600 hover:bg-rose-500 text-white"
                        }`}
                      >
                        {isPresent ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                        <span>{status}</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end pt-2">
          <button
            type="submit"
            disabled={saving || students.length === 0}
            data-testid="save-attendance-submit-btn"
            className="px-6 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-mono text-xs font-semibold shadow-md transition-all flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Saving Attendance...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Save Day {dayNumber} Attendance</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
